import { chipClasses } from '@mui/material/Chip';
import { extendTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import cssVarJson from './export.json';
import InterItalic from './fonts/Inter/InterVariable-Italic.woff2';
import Inter from './fonts/Inter/InterVariable.woff2';
import { fixThemeOptions } from '../../utils/theme';
const CSS_VAR_PREFIX = 'umeds'; // Needs to be unique to support theme nesting
const baseFontFamily = 'Inter, Helvetica, Arial, sans-serif';
const PARTIAL_FILLED_CHIP_COLORS = ['primary', 'secondary', 'success', 'info', 'warning', 'error'];
// MUI's clickable, deletable and focus rules swap in the solid colour; the tint stays and darkens with the ink overlay.
const partialFilledChipStyle = (theme, { text, background }) => {
    const overlay = (theme.vars ?? theme).palette.action.focus;
    return {
        backgroundColor: background,
        color: text,
        [`&.${chipClasses.clickable}:hover, &.${chipClasses.focusVisible}`]: {
            background: `linear-gradient(${overlay}, ${overlay}), ${background}`,
        },
        [`& .${chipClasses.deleteIcon}`]: { color: 'inherit', opacity: 0.7 },
        [`& .${chipClasses.deleteIcon}:hover, & .${chipClasses.deleteIcon}:active`]: { color: 'inherit', opacity: 1 },
    };
};
const customizations = {
    cssVarPrefix: CSS_VAR_PREFIX,
    spacing: (factor) => `${0.25 * factor}rem`,
    typography: {
        fontFamily: baseFontFamily,
    },
    components: {
        // MuiAlert overrides to fix the broken logic in extend theme.
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-error-light)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`, // Different color for message text
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardWarning: {
                    backgroundColor: '#FFF4E5',
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardInfo: {
                    backgroundColor: '#E5F6FD',
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardSuccess: {
                    backgroundColor: '#EDF7ED',
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedError: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedWarning: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedInfo: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedSuccess: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
        @font-face {
          font-family: Inter;
          font-style: normal;
          font-weight: 100 900;
          font-display: swap;
          src: url(${Inter}) format("woff2");
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        @font-face {
          font-family: Inter;
          font-style: italic;
          font-weight: 100 900;
          font-display: swap;
          src: url(${InterItalic}) format("woff2");
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        .Toastify__toast--success { background-color: #EDF7ED; }
        .Toastify__toast--error { background-color: #F9E6E6; }
      `,
        },
        MuiChip: {
            // uMeds has no partial_filled tokens in Figma yet, so these derive from the palette until design defines them.
            variants: [
                {
                    props: { variant: 'partial_filled', color: 'default' },
                    style: ({ theme }) => {
                        const { palette } = theme.vars ?? theme;
                        return partialFilledChipStyle(theme, { text: palette.text.primary, background: palette.action.selected });
                    },
                },
                // One formula for every colour: uMeds' `.light`/`.dark` aren't a readable pair for primary and secondary.
                ...PARTIAL_FILLED_CHIP_COLORS.map((color) => ({
                    props: { variant: 'partial_filled', color },
                    style: ({ theme }) => {
                        const { palette } = theme.vars ?? theme;
                        return partialFilledChipStyle(theme, {
                            text: `color-mix(in srgb, ${palette[color].main} 55%, black)`,
                            background: `color-mix(in srgb, ${palette[color].main} 12%, transparent)`,
                        });
                    },
                })),
            ],
            // Temporary fixes to the export. Once the figma is fixed and exported properly, these should be removed
            styleOverrides: {
                label: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                sizeLarge: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px', // Export bug causing button hover padding change
                }),
                sizeMedium: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px',
                }),
                sizeSmall: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px',
                }),
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: (props) => ({
                    ...props.theme.typography.caption,
                }),
            },
        },
        MuiLink: {
            styleOverrides: {
                button: {
                    verticalAlign: 'unset',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    // Make the title a grid container
                    display: 'grid',
                    alignItems: 'flex-start',
                    gridTemplateColumns: '1fr',
                    // Conditionally apply rowGap ONLY if DialogDescription is a child
                    '&:has(> [class*="MuiDialogDescription-root"])': {
                        rowGap: theme.spacing(2),
                    },
                    // Conditionally apply column changes if an icon is present
                    '&:has(> svg)': {
                        gridTemplateColumns: 'min-content 1fr',
                        columnGap: theme.spacing(2),
                    },
                    // Icon placement
                    '& > svg': {
                        gridColumn: '1',
                        gridRow: '1 / span 2',
                    },
                    // Description placement
                    '&:has(> svg) > [class*="MuiDialogDescription-root"]': {
                        gridColumn: '2',
                        gridRow: '2',
                    },
                    // Icon styles
                    '& > .MuiSvgIcon-root': {
                        fontSize: 'inherit',
                        // This translate is a bit of a hack but also seemingly the only way to align the icon with the title text due to font line height being larger than font size
                        transform: `translateY(${theme.spacing(1)})`,
                    },
                    // If an IconButton exists in the DialogTitle (AKA a dialog close icon button), add right padding so text doesn't overlap it
                    '&:has(> [class*="MuiIconButton-root"])': {
                        paddingRight: theme.spacing(11),
                    },
                    // Title font styles (Mobile)
                    ...theme.typography.subtitle2,
                    padding: `${theme.spacing(4)} ${theme.spacing(4)} 0px`,
                    fontWeight: 600,
                    // Title font styles (Desktop)
                    [theme.breakpoints.up('md')]: {
                        ...theme.typography.subtitle1,
                        padding: `${theme.spacing(4)} ${theme.spacing(6)} 0px`,
                        fontWeight: 600,
                    },
                }),
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    // NOTE: This overrides the Mui default global styling override which would remove the padding top if placed after a DialogTitle
                    '.MuiDialogTitle-root + &': {
                        padding: `${theme.spacing(4)} ${theme.spacing(4)}`,
                        [theme.breakpoints.up('md')]: {
                            padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
                        },
                    },
                    padding: `${theme.spacing(4)} ${theme.spacing(4)}`,
                    [theme.breakpoints.up('md')]: {
                        padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
                    },
                }),
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: `var(--${CSS_VAR_PREFIX}-palette-text-primary)`,
                    ...theme.typography.body2,
                }),
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }) => ({
                    display: 'flex',
                    gap: theme.spacing(2),
                    padding: theme.spacing(2),
                    '& .MuiButton-root': {
                        margin: '0',
                    },
                    flexDirection: 'row',
                }),
            },
        },
    },
};
// TODO: Reenable Dark Mode once MUI6 upgrade issues have been resolved
export const uMedsJSON = fixThemeOptions(deepmerge(cssVarJson, customizations), {
    disableDarkMode: true,
});
export const uMeds = extendTheme(uMedsJSON);
export default uMeds;
//# sourceMappingURL=data:application/json;base64,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